import type { ReactNode } from 'react'
import { PageHeader } from '@/components/app/PageHeader'
import { Card } from '@/components/ui/Card'

/**
 * Chrome for a review page. Knows nothing about rows — which is why Media can
 * render a grid through the same shell that Programs renders a table through
 * (docs/06-component-refactor.md §4.2).
 *
 * This is deliberately not a config-driven `<EntityReviewPage columns={...} />`.
 * That shape breaks the moment one surface needs a grid and another needs two
 * sections, and degenerates into a 400-line prop interface.
 */
export function ReviewSurface({
  title,
  description,
  headerActions,
  filters,
  tabs,
  bulkBar,
  aside,
  children,
}: {
  title: string
  /** States the gate in-context: "Only approved X are retrievable by the agent." */
  description: string
  headerActions?: ReactNode
  /** Search input, selects, segmented controls. */
  filters?: ReactNode
  tabs: ReactNode
  bulkBar?: ReactNode
  /**
   * Left rail, for the one surface that is master/detail rather than a single
   * pane — Programs and its segment rail. Omitted, the content is full width.
   */
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <>
      <PageHeader title={title} description={description} actions={headerActions} />
      <div className="flex flex-wrap items-start gap-5">
        {aside && <div className="w-[240px] shrink-0 max-lg:w-full">{aside}</div>}
        <div className="min-w-0 flex-1">
          {/* `overflow-clip`, not `-hidden`: see BulkActionBar. */}
          <Card className="overflow-clip">
            {filters && (
              <div className="flex flex-wrap items-center gap-2 px-4 pt-3">{filters}</div>
            )}
            <div className="px-4">{tabs}</div>
            {children}
            {bulkBar}
          </Card>
        </div>
      </div>
    </>
  )
}
